import React, { useEffect, useRef, useState } from "react";
import { API, authedFetch } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { uploadWithRetry } from "../lib/uploadWithRetry";
import {
  isPendingPath, newPendingKey, savePending, getPending, removePending,
  notifyPendingChanged,
} from "../lib/photoQueue";
import useFileDropzone from "../lib/useFileDropzone";
import { ImagePlus, X, Loader2, Pencil, RotateCw, CloudOff } from "lucide-react";
import { openLightbox } from "./Lightbox";
import PhotoAnnotator from "./PhotoAnnotator";

/**
 * PhotoUploader — multi-photo (max N). Offline-resilient: failed uploads are
 * kept in IndexedDB and rendered locally with a "in coda" badge + retry button.
 *
 * @param value: string[] of storage paths (may include "pending::<uuid>" entries)
 * @param onChange(newArr): parent state setter
 * @param max: max photos (default 3)
 * @param readOnly: when true, only renders photos (no upload/remove)
 */
export default function PhotoUploader({ value = [], onChange, max = 3, readOnly = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [urls, setUrls] = useState({}); // path/key -> blobUrl

  // Resolve previews: remote paths via authedFetch, pending keys via IDB
  useEffect(() => {
    let cancelled = false;
    const blobs = {};
    (async () => {
      for (const p of value) {
        if (urls[p]) continue;
        try {
          let b = null;
          if (isPendingPath(p)) {
            const rec = await getPending(p);
            b = rec?.blob || null;
          } else {
            const res = await authedFetch(`${API}/files/${p}`);
            if (res.ok) b = await res.blob();
          }
          if (!b) continue;
          const u = URL.createObjectURL(b);
          if (cancelled) URL.revokeObjectURL(u);
          else blobs[p] = u;
        } catch { /* ignore */ }
      }
      if (!cancelled && Object.keys(blobs).length) {
        setUrls((prev) => ({ ...prev, ...blobs }));
      }
    })();
    return () => { cancelled = true; };
  }, [value]);

  useEffect(() => () => {
    Object.values(urls).forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* */ } });
  }, []);

  // Listen for global flush events that swap pending → real path
  useEffect(() => {
    const handler = (e) => {
      const { key, newPath } = e.detail || {};
      if (!key || !newPath) return;
      if (value.includes(key)) {
        onChange(value.map((p) => (p === key ? newPath : p)));
      }
    };
    window.addEventListener("uc:pending-photo-flushed", handler);
    return () => window.removeEventListener("uc:pending-photo-flushed", handler);
  }, [value, onChange]);

  const pick = () => inputRef.current?.click();

  const doUpload = async (file, replacePath = null) => {
    setError("");
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      try {
        const data = await uploadWithRetry(compressed);
        if (replacePath) {
          onChange(value.map((p) => (p === replacePath ? data.path : p)));
        } else {
          onChange([...value, data.path]);
        }
      } catch {
        const key = newPendingKey();
        await savePending(key, compressed, { name: compressed.name || file.name || "foto.jpg" });
        notifyPendingChanged();
        if (replacePath) {
          onChange(value.map((p) => (p === replacePath ? key : p)));
        } else {
          onChange([...value, key]);
        }
        setError("Offline · foto salvata sul dispositivo, verrà caricata appena torna la connessione");
      }
    } catch (err) {
      setError(err?.message || "Upload fallito");
    }
    setUploading(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (value.length >= max) return;
    await doUpload(file);
  };

  const remove = async (p) => {
    if (isPendingPath(p)) { await removePending(p); notifyPendingChanged(); }
    onChange(value.filter((x) => x !== p));
  };

  const retryOne = async (p) => {
    if (!isPendingPath(p)) return;
    const rec = await getPending(p);
    if (!rec) { onChange(value.filter((x) => x !== p)); return; }
    setError("");
    setUploading(true);
    try {
      const data = await uploadWithRetry(rec.blob, { filename: rec.name });
      await removePending(p);
      notifyPendingChanged();
      onChange(value.map((x) => (x === p ? data.path : x)));
    } catch {
      setError("Ancora offline · la foto resta in coda, riproveremo automaticamente");
    }
    setUploading(false);
  };

  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [annotatorFor, setAnnotatorFor] = useState(null);
  const [annotatorSrc, setAnnotatorSrc] = useState(null);

  const openAnnotatorFor = (path) => {
    const src = urls[path];
    if (!src) return;
    setAnnotatorFor(path);
    setAnnotatorSrc(src);
    setAnnotatorOpen(true);
  };

  const handleAnnotated = async (blob) => {
    setAnnotatorOpen(false);
    const file = new File([blob], "annotata.jpg", { type: "image/jpeg" });
    await doUpload(file, annotatorFor);
    setAnnotatorFor(null);
    setAnnotatorSrc(null);
  };

  const handleDroppedFiles = async (files) => {
    // Multi-file drop: upload each one sequentially until we hit max
    for (const f of files) {
      if (value.length >= max) break;
      await doUpload(f);
    }
  };
  const { handlers: dropHandlers, isOver, isDesktop } = useFileDropzone({
    onFiles: handleDroppedFiles,
    accept: ["png", "jpg", "jpeg", "webp", "heic", "gif"],
    multiple: true,
    disabled: readOnly || uploading || value.length >= max,
  });

  return (
    <div data-testid="photo-uploader">
      <div
        {...dropHandlers}
        className={`flex gap-3 flex-wrap transition-all ${
          isOver ? "ring-2 ring-[#ff5a00] ring-offset-2 bg-[#fff7f0] rounded-xl p-2 -m-2" : ""
        }`}
      >
        {value.map((p) => {
          const pending = isPendingPath(p);
          return (
            <div key={p} className="relative w-24 h-24 border-[1.5px] border-black bg-neutral-100 overflow-hidden bru-shadow-sm rounded-lg">
              {urls[p] ? (
                <button
                  type="button"
                  onClick={() => !pending && openLightbox(p, "Foto")}
                  className="w-full h-full"
                  data-testid="photo-open-btn"
                  title={pending ? "Foto in coda (offline)" : "Apri foto"}
                >
                  <img src={urls[p]} alt="foto" className={`w-full h-full object-cover ${pending ? "" : "cursor-zoom-in"}`} />
                </button>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
                </div>
              )}
              {pending && (
                <div className="absolute inset-x-0 bottom-0 bg-[#ffcc00] text-black text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 flex items-center justify-center gap-1" data-testid="photo-pending-badge">
                  <CloudOff className="w-3 h-3" />
                  In coda
                </div>
              )}
              {!readOnly && (
                <>
                  {pending ? (
                    <button
                      type="button"
                      onClick={() => retryOne(p)}
                      disabled={uploading}
                      className="absolute bottom-6 right-1 bg-[#ff5a00] text-white w-6 h-6 flex items-center justify-center hover:bg-[#ff7a30] rounded-full disabled:opacity-60"
                      data-testid="photo-retry-btn"
                      title="Riprova upload"
                    >
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openAnnotatorFor(p)}
                      className="absolute bottom-1 right-1 bg-[#ff5a00] text-white w-6 h-6 flex items-center justify-center hover:bg-[#ff7a30] rounded-full"
                      data-testid="photo-edit-btn"
                      title="Modifica foto (pennarello)"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(p)}
                    className="absolute top-1 right-1 bg-black text-white w-6 h-6 flex items-center justify-center hover:bg-[#ff3b30] rounded-full"
                    data-testid="photo-remove-btn"
                    aria-label="Rimuovi foto"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          );
        })}
        {!readOnly && value.length < max && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            className={`w-24 h-24 border-[1.5px] border-dashed flex flex-col items-center justify-center gap-1 disabled:opacity-60 rounded-lg transition-colors ${
              isOver ? "border-[#ff5a00] bg-[#fff7f0]" : "border-black bg-white hover:bg-[#fff7f0]"
            }`}
            data-testid="photo-add-btn"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ImagePlus className="w-5 h-5" />
                <span className="text-[10px] uppercase font-bold tracking-wider">{isOver ? "Rilascia" : "Aggiungi"}</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid="photo-file-input"
      />
      {!readOnly && (
        <div className="text-xs text-neutral-500 mt-2">
          Max {max} foto · jpg · png · webp · le foto vengono compresse automaticamente
          {isDesktop && " · trascina le foto qui per caricarle"}
        </div>
      )}
      {error && <div className="text-xs text-[#a36b00] mt-1" data-testid="photo-uploader-error">{error}</div>}
      {annotatorOpen && annotatorSrc && (
        <PhotoAnnotator
          src={annotatorSrc}
          onSave={handleAnnotated}
          onCancel={() => { setAnnotatorOpen(false); setAnnotatorFor(null); setAnnotatorSrc(null); }}
        />
      )}
    </div>
  );
}
